import React, { useMemo, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
// Importa tu método de request
import { createTicket, createKontrolTicket } from "../request/request";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { KontrolEdgeServiceSelection } from "./shared/KontrolEdgeServiceSelection";
import { useServiceSelection } from "../hooks/useServiceSelection";
import { useLoadServiceItems } from "../hooks/useLoadServiceItems";
import { useWizardTabs } from "../hooks/useWizardTabs";
import {
	extractUniqueCustomers,
	filterServicesByCustomer,
	isServiceSelectionValid,
	stripNonAlphanumeric,
	isValidEmail,
} from "../utils/tickets";

interface LabelValue {
	label: string;
	value: string;
}

interface HelpRequestModalProps {
	isOpen: boolean;
	onClose: () => void;
	onTicketCreated?: () => void;
}

// Opcional: lista de códigos de país
const countryCodes: LabelValue[] = [
	{ label: "United States (+1)", value: "+1" },
	{ label: "Mexico (+52)", value: "+52" },
	{ label: "Canada (+1)", value: "+1" },
];

// Service selection only exists for Kontrol Edge: it is the step that ties the
// ticket to the inventory, and only KS carries the Service ID field (CTX-533).
// The other products go straight to the general info step, as before.
const SERVICE_SELECTION_TAB = {
	id: "serviceSelection",
	label: "Service selection",
};
const BASE_TABS = [
	{ id: "generalInfo", label: "General Info" },
	{ id: "attachments", label: "Attachments & Response" },
];

export const HelpRequestModal: React.FC<HelpRequestModalProps> = ({
	isOpen,
	onClose,
	onTicketCreated,
}) => {
	const api = useAxiosAuth0();
	const { tenantId, partnerId } = useAppSelector((state) => state.selection);
	const isPartnerView = tenantId === partnerId;

	// --- ESTADOS DE FORMULARIO ---
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [attachmentFiles, setAttachmentFiles] = useState<FileList | null>(null);

	// Datos de contacto extra
	const [sendResponse, setSendResponse] = useState(false);
	const [contactName, setContactName] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [selectedCountryCode, setSelectedCountryCode] = useState("+52"); // Ej: por defecto +52
	const [contactPhoneNumber, setContactPhoneNumber] = useState("");

	// --- CONTROL DE UI ---
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertVariant, setAlertVariant] = useState<"success" | "error">(
		"success"
	);
	const [alertMessage, setAlertMessage] = useState("");
	const [isSaving, setIsSaving] = useState(false);

	// Overlay si hay loading
	const isLoadingSomething = isSaving;
	const overlay = useProcessingOverlay(isLoadingSomething);

	// Check if the selected product is "Kontrol Edge"
	const globalProduct = useAppSelector(
		(state) => state.product.selectedProduct
	);
	const isKontrolEdge = globalProduct === "Kontrol Edge";

	// --- SELECCIÓN DE SERVICIO (solo Kontrol Edge) ---
	const serviceSelection = useServiceSelection();
	const { items: serviceItems } = useLoadServiceItems(
		api,
		tenantId,
		isKontrolEdge
	);

	const uniqueCustomers = useMemo(
		() => extractUniqueCustomers(serviceItems),
		[serviceItems]
	);

	// Outside partner view the user only ever sees their own services, so there
	// is nothing to narrow down.
	const filteredServiceItems = useMemo(() => {
		if (!isPartnerView) return serviceItems;
		return filterServicesByCustomer(
			serviceItems,
			serviceSelection.selectedCustomerId
		);
	}, [serviceItems, serviceSelection.selectedCustomerId, isPartnerView]);

	const tabs = isKontrolEdge ? [SERVICE_SELECTION_TAB, ...BASE_TABS] : BASE_TABS;
	const firstTabId = tabs[0].id;

	// --- VALIDACIONES BÁSICAS ---
	const hasValidServiceSelection = () =>
		!isKontrolEdge ||
		isServiceSelectionValid(
			isPartnerView,
			serviceSelection.selectedCustomerId,
			serviceSelection.selectedServiceIds
		);

	const isGeneralInfoValid = () => {
		// Title >= 5 chars sin espacios/puntuación
		// Description >= 10 chars sin espacios/puntuación
		const titleClean = stripNonAlphanumeric(title);
		const descClean = stripNonAlphanumeric(description);
		return titleClean.length >= 5 && descClean.length >= 10;
	};

	const isAttachmentsValid = () => {
		// Solo validamos contacto si toggle = true
		if (!sendResponse) return true;
		const minName = contactName.trim().length >= 3;
		const validEmail = isValidEmail(contactEmail.trim());
		return minName && validEmail;
	};

	// --- REGLAS DE AVANCE ENTRE TABS ---
	const {
		activeTab,
		go: goTab,
		canActivate,
		setActiveTab,
	} = useWizardTabs(firstTabId, {
		serviceSelection: () => true,
		generalInfo: () => hasValidServiceSelection(),
		attachments: () => hasValidServiceSelection() && isGeneralInfoValid(),
	});

	const handleChangeTab = (tabId: string) => goTab(tabId);

	// --- HANDLERS ---
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			setAttachmentFiles(e.target.files);
		}
	};

	const handleCloseModal = () => {
		resetFormState();
		onClose();
	};

	const resetFormState = () => {
		setTitle("");
		setDescription("");
		setAttachmentFiles(null);
		setSendResponse(false);
		setContactName("");
		setContactEmail("");
		setSelectedCountryCode("+52");
		setContactPhoneNumber("");
		serviceSelection.reset();
		// Bypasses the guards on purpose: a reset form fails them, and the first
		// tab is where the next ticket has to start.
		setActiveTab(firstTabId);
	};

	const handleCustomerSelect = (customerNameDisplay: string) => {
		serviceSelection.handleCustomerSelect(
			customerNameDisplay,
			uniqueCustomers
		);
	};

	const handleServiceSelect = (selectedLabels: string[]) => {
		serviceSelection.handleServiceSelect(selectedLabels, filteredServiceItems);
	};

	// --- GUARDAR TICKET ---
	const handleSave = async () => {
		if (!isAttachmentsValid()) return;
		setIsSaving(true);
		try {
			// Construimos el payload
			if (isKontrolEdge) {
				if (!hasValidServiceSelection()) {
					setAlertVariant("error");
					setAlertMessage(
						isPartnerView && !serviceSelection.selectedCustomerId
							? "Please select a customer and at least one service before creating the ticket."
							: "Please select at least one service before creating the ticket."
					);
					setAlertOpen(true);
					setIsSaving(false);
					return;
				}

				const finalPayload = {
					requesttype: "HELP_REQUEST",
					summary: title,
					description,
					subscriptionID: serviceSelection.selectedSubscriptionIds,
					serviceType: serviceSelection.selectedServiceTypes,
					// Partner view: the customer picked above. Customer view: the
					// tenant in session, which is the end customer already.
					end_customer: isPartnerView
						? (serviceSelection.selectedCustomerId as number)
						: tenantId,
					files: attachmentFiles ? Array.from(attachmentFiles) : [],
				};
				await createKontrolTicket(api, finalPayload);
				if (onTicketCreated) {
					onTicketCreated();
				}
			} else {
				const finalPayload = {
					Tipo: "Help Request",
					Prioridad: 1,
					Resumen: title,
					Descripcion: description,
					files: attachmentFiles ? Array.from(attachmentFiles) : [],
					Nombre_de_contacto: contactName,
					Correo: contactEmail,
					phone_number: `${selectedCountryCode}${contactPhoneNumber.replace(
						/\s+/g,
						""
					)}`,
				};
				await createTicket(api, finalPayload);
			}
			setAlertVariant("success");
			setAlertMessage("Help Request created successfully!");
			setAlertOpen(true);
		} catch (err) {
			console.error("Error creating help request =>", err);
			setAlertVariant("error");
			setAlertMessage(
				"A problem occurred while creating the request, please try again."
			);
			setAlertOpen(true);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<GenericModal
			isOpen={isOpen}
			onClose={handleCloseModal}
			closeOnBackdropClick={false}
			size="lg"
			title={
				<div className="flex items-center gap-2">
					<span className="text-semantic-text-primary">Help Request</span>
					<GenericTooltip
						text="If you have any questions you can contact us through this medium."
						position="top"
					/>
				</div>
			}
		>
			{/* Encabezado de tabs */}
			<div className="border-b border-gray-200 mb-4">
				<nav className="flex space-x-4" aria-label="Tabs">
					{tabs.map((tab) => {
						const disabled = !canActivate(tab.id);
						return (
							<button
								key={tab.id}
								onClick={() => handleChangeTab(tab.id)}
								disabled={disabled}
								className={`
                  px-3 py-2 text-sm font-medium focus:outline-none
                  ${
										activeTab === tab.id
											? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary"
											: disabled
											? "text-gray-400 cursor-not-allowed"
											: "text-gray-500 hover:text-gray-700"
									}
                `}
							>
								{tab.label}
							</button>
						);
					})}
				</nav>
			</div>

			{/* TAB 0 (solo Kontrol Edge): Service selection */}
			{isKontrolEdge && activeTab === "serviceSelection" && (
				<div className="space-y-4 text-sm text-gray-700">
					<KontrolEdgeServiceSelection
						isPartnerView={isPartnerView}
						uniqueCustomers={uniqueCustomers}
						filteredServiceItems={filteredServiceItems}
						selectedCustomerId={serviceSelection.selectedCustomerId}
						selectedCustomerNameDisplay={
							serviceSelection.selectedCustomerNameDisplay
						}
						selectedServiceLabels={serviceSelection.selectedServiceLabels}
						onCustomerSelect={handleCustomerSelect}
						onServiceSelect={handleServiceSelect}
						servicesTooltip="Select the service(s) your question is about."
					/>

					<div className="flex justify-end pt-2">
						<GenericButton
							label="Next"
							variant="brand"
							onClick={() => handleChangeTab("generalInfo")}
							disabled={!hasValidServiceSelection()}
						/>
					</div>
				</div>
			)}

			{/* TAB 1: General Info */}
			{activeTab === "generalInfo" && (
				<div className="space-y-4 text-sm text-gray-700">
					<div>
						<label className="block mb-1 font-medium flex items-center gap-1 text-semantic-text-secondary">
							Title <span className="text-red-500">*</span>
							<GenericTooltip
								text="At least 5 characters (ignoring spaces & punctuation)."
								position="top"
							/>
						</label>
						<GenericInput
							placeholder="Write a brief description"
							value={title}
							onChange={setTitle}
						/>
					</div>

					<div>
						<label className="block mb-1 font-medium flex items-center gap-1 text-semantic-text-secondary">
							Description <span className="text-red-500">*</span>
							<GenericTooltip
								text="At least 10 characters (ignoring spaces & punctuation)."
								position="top"
							/>
						</label>
						<textarea
							className="w-full h-24 px-3 py-2 border border-gray-300 rounded-sm bg-semantic-bg-primary text-sm text-semantic-text-secondary"
							placeholder="Description of the problem (required)"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>

					<div className="flex justify-end pt-2 gap-2">
						{isKontrolEdge && (
							<GenericButton
								label="Back"
								variant="secondary"
								onClick={() => handleChangeTab("serviceSelection")}
							/>
						)}
						<GenericButton
							label="Next"
							variant="brand"
							onClick={() => handleChangeTab("attachments")}
							disabled={!isGeneralInfoValid()}
						/>
					</div>
				</div>
			)}

			{/* TAB 2: Attachments & Response */}
			{activeTab === "attachments" && (
				<div className="space-y-4 text-sm text-gray-700">
					<div>
						<label className="block mb-1 font-medium text-semantic-text-secondary">
							Attach (Drag and drop screenshots or browse your device)
						</label>
						<div className="flex flex-col items-start gap-2">
							<label
								htmlFor="file-upload"
								className="inline-flex items-center justify-center cursor-pointer px-4 py-2 min-w-[140px] border border-gray-300 rounded-sm bg-gray-50 text-sm whitespace-nowrap hover:bg-gray-100"
							>
								{attachmentFiles ? "Change Files" : "Choose Files"}
							</label>
							<input
								id="file-upload"
								type="file"
								className="hidden"
								multiple
								onChange={handleFileChange}
							/>
							<span className="text-sm text-gray-500 break-all">
								{attachmentFiles
									? Array.from(attachmentFiles)
											.map((file) => file.name)
											.join(", ")
									: "No file chosen"}
							</span>
						</div>
					</div>

					<div className="space-y-2">
						<div className="flex items-center justify-between mt-3">
							<span className="font-medium text-sm text-semantic-text-secondary">
								I want to send a response of the case to another person
							</span>
							<GenericSwitch
								checked={sendResponse}
								onChange={setSendResponse}
							/>
						</div>

						{sendResponse && (
							<div className="space-y-4 mt-2 border border-gray-200 rounded p-3">
								<div className="flex flex-col sm:flex-row gap-4">
									<div className="w-full sm:w-1/2">
										<label className="block mb-1 font-medium flex items-center gap-1">
											Contact Name <span className="text-red-500">*</span>
											<GenericTooltip
												text="At least 3 characters."
												position="top"
											/>
										</label>
										<GenericInput
											placeholder="Write the name here"
											value={contactName}
											onChange={setContactName}
											fullWidth
										/>
									</div>
									<div className="w-full sm:w-1/2">
										<label className="block mb-1 font-medium flex items-center gap-1">
											Contact Email <span className="text-red-500">*</span>
											<GenericTooltip
												text="Must be a valid email format (e.g. name@domain.com)."
												position="top"
											/>
										</label>
										<GenericInput
											placeholder="Write the email here"
											value={contactEmail}
											onChange={setContactEmail}
											type="email"
											fullWidth
										/>
									</div>
								</div>

								<div className="flex flex-col sm:flex-row gap-4">
									<div className="w-full sm:w-1/2">
										<label className="block mb-1 font-medium">
											Country Code
										</label>
										<GenericDropdown
											items={countryCodes.map((c) => c.label)}
											value={
												countryCodes.find(
													(c) => c.value === selectedCountryCode
												)?.label || "United States (+1)"
											}
											onSelect={(label) => {
												const found = countryCodes.find(
													(c) => c.label === label
												);
												if (found) {
													setSelectedCountryCode(found.value);
												}
											}}
											defaultLabel="Select country code"
											minWidthClass=""
											maxWidthClass=""
											widthClass="w-full"
										/>
									</div>
									<div className="w-full sm:w-1/2">
										<label className="block mb-1 font-medium">
											Phone Number
										</label>
										<GenericInput
											placeholder="Phone number"
											value={contactPhoneNumber}
											onChange={setContactPhoneNumber}
											fullWidth
										/>
									</div>
								</div>
							</div>
						)}
					</div>

					<div className="flex justify-end pt-2 gap-2">
						<GenericButton
							label="Back"
							variant="secondary"
							onClick={() => handleChangeTab("generalInfo")}
						/>
						<GenericButton
							label="Save"
							variant="brand"
							onClick={handleSave}
							disabled={!isAttachmentsValid() || isSaving}
						/>
					</div>
				</div>
			)}

			{/* Overlay por si hay loading */}
			{overlay}

			{/* Alert de éxito/error */}
			<GenericAlert
				isOpen={alertOpen}
				onClose={() => setAlertOpen(false)}
				onConfirm={() => {
					setAlertOpen(false);
					if (alertVariant === "success") {
						handleCloseModal();
					}
				}}
				variant={alertVariant}
				title={alertVariant === "success" ? "Help Request Created" : "Error"}
				description={alertMessage}
				confirmLabel="OK"
			/>
		</GenericModal>
	);
};

export default HelpRequestModal;
